import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';

import { Loader2 } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/providers/auth-provider';
import { supabase } from '@/services';
import { useTranslation } from 'react-i18next';

export default function AcceptInvitationPage() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const navigate = useNavigate();
  const { session } = useAuth();
  const { t } = useTranslation();
  const { toast } = useToast();
  const acceptInvitation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: t('welcomeAboard'),
        description: t('accountSetupSuccess'),
        duration: 5000,
      });
      navigate('/');
    },
    onError: (error: Error) => {
      toast({
        title: t('oops'),
        description: error.message,
        variant: 'destructive',
        duration: 5000,
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast({
        title: t('passwordsDontMatch'),
        description: t('passwordsMatchError'),
        variant: 'destructive',
        duration: 5000,
      });
      return;
    }
    acceptInvitation.mutate();
  };

  if (session?.user?.role !== 'authenticated') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-center">
              {t('invalidInvitation')}
            </CardTitle>
            <CardDescription className="text-center">
              {t('invalidInvitationDescription')}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-center">
            <div className="flex justify-center mb-4">
              <img src="/logo.svg" alt="rise logo" width={100} height={100} />
            </div>
          </CardTitle>
          <CardDescription className="text-center">{t('welcomeMessage')}</CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="space-y-2">
                <Label htmlFor="password">{t('newPassword')}</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">{t('confirmNewPassword')}</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={8}
                />
              </div>
            </motion.div>
          </CardContent>
          <CardFooter>
            <Button
              type="submit"
              className="w-full"
              disabled={acceptInvitation.isPending}
            >
              {acceptInvitation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('settingUpAccount')}
                </>
              ) : (
                t('completeAccountSetup')
              )}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
